import React, { useState } from 'react';
import { SettingsOutlined } from '@duxton-web/icons';
import { Button, ButtonProps, ModalProps } from '@duxton-web/ui';
import scribe from '@grab-x/scribe-web-sdk';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { newVariablesEvents } from '@/common/Scribe/EventsList';
import { VariableSettingsAction } from '@/features/variable/constants';
import VariableContextProvider from '@/features/variable/contexts/variable-context';
import CreateVariableModal from '@/features/variable/settings/create-variable-modal';
import VariableDetail from '@/features/variable/settings/detail';
import EditVariableModal from '@/features/variable/settings/edit-variable-modal';
import { getVariableEditPathname } from '@/features/variable/utils';

const VariableSettingsModal = ({
  variableName,
  open,
  onCancel,
  onArchiveOk = () => {},
  onDeleteOk = () => {},
}: {
  variableName: string;
  onArchiveOk?: () => void;
  onDeleteOk?: () => void;
} & ModalProps) => {
  if (variableName) {
    return (
      <EditVariableModal
        variableName={variableName}
        onArchiveOk={onArchiveOk}
        onDeleteOk={onDeleteOk}
      />
    );
  }

  return <CreateVariableModal open={open} onCancel={onCancel} />;
};

interface VariableSettingsProps {
  variableName: string;
  onArchiveOk?: () => void;
  onDeleteOk?: () => void;
}

const VariableSettings = ({
  variableName,
  type = 'secondary',
  shape = 'circle',
  icon = <SettingsOutlined />,
  children,
  disabled,
  onArchiveOk = () => {},
  onDeleteOk = () => {},
}: VariableSettingsProps & ButtonProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const { pathname } = location;

  const [open, setOpen] = useState<boolean>(false);

  return (
    <VariableContextProvider>
      <Button
        disabled={disabled}
        data-testid="variable-settings-button"
        shape={shape}
        size="small"
        type={type}
        icon={icon}
        onClick={() => {
          queryClient.invalidateQueries({
            queryKey: ['planner', 'variable'],
          });

          if (variableName) {
            scribe.sendEvent(newVariablesEvents.clickEditVariable.name, {
              STATE_NAME: newVariablesEvents.clickEditVariable.state,
            });

            const nextPathname = getVariableEditPathname(
              pathname,
              variableName,
              VariableSettingsAction.EDIT,
            );
            navigate(
              {
                ...location,
                pathname: nextPathname,
              },
              { replace: true },
            );
          } else {
            scribe.sendEvent(newVariablesEvents.clickCreateVariable.name, {
              STATE_NAME: newVariablesEvents.clickCreateVariable.state,
            });

            setOpen(true);
          }
        }}
      >
        {children}
      </Button>
      <VariableSettingsModal
        variableName={variableName}
        open={open}
        onCancel={() => setOpen(false)}
        onArchiveOk={onArchiveOk}
        onDeleteOk={onDeleteOk}
      />
      <VariableDetail style={{ display: 'none' }} />
    </VariableContextProvider>
  );
};

export default VariableSettings;
